import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { message } from 'antd';

import { NavBar } from '@/components/NavBar';
import { useResponsive } from '@/hooks/useResponsive';

import styles from './index.module.less';

// Email format validation regex
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const WaitList: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  // Validate email format
  const validateEmail = (email: string): boolean => {
    if (!email.trim()) {
      setEmailError(t('waitlist.errorMessages.emailRequired'));
      return false;
    }
    if (!EMAIL_REGEX.test(email.trim())) {
      setEmailError(t('waitlist.errorMessages.emailInvalid'));
      return false;
    }
    setEmailError('');
    return true;
  };

  // Submit email to waitlist
  const submitToWaitlist = async (email: string) => {
    try {
      const response = await fetch('/api/waitlist/join', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Failed to submit to waitlist:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    if (!validateEmail(email)) {
      return;
    }

    setIsSubmitting(true);
    try {
      await submitToWaitlist(email);
      setSubmittedEmail(email);
      setIsSuccess(true);
      setEmail('');
      setEmailError('');
    } catch (error) {
      message.error(t('waitlist.errorMessages.submitError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    // Clear error state when user starts typing
    if (emailError && value.trim()) {
      setEmailError('');
    }
  };

  const handleEmailBlur = () => {
    // Validate email format on blur
    if (email.trim()) {
      validateEmail(email);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className={`${styles.container} ${isMobile ? styles.mobileContainer : ''}`}>
      <NavBar />
      <div className={`${styles.content} ${isMobile ? styles.mobileContent : ''}`}>
        {!isSuccess ? (
          <>
            <div className={`${styles.textSection} ${isMobile ? styles.mobileTextSection : ''}`}>
              <h1 className={`${styles.title} ${isMobile ? styles.mobileTitle : ''}`}>
                {t('waitlist.title')}
              </h1>
              <p className={`${styles.description} ${isMobile ? styles.mobileDescription : ''}`}>
                {t('waitlist.description')}
              </p>
            </div>
            <div className={`${styles.inputSection} ${isMobile ? styles.mobileInputSection : ''}`}>
              <div
                className={`${styles.inputContainer} ${isMobile ? styles.mobileInputContainer : ''}`}
              >
                <div
                  className={`${styles.inputWrapper} ${isMobile ? styles.mobileInputWrapper : ''}`}
                >
                  <input
                    type="email"
                    placeholder={t('waitlist.emailPlaceholder')}
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    onKeyPress={handleKeyPress}
                    className={`${styles.emailInput} ${isMobile ? styles.mobileEmailInput : ''} ${emailError ? styles.emailInputError : ''}`}
                    disabled={isSubmitting}
                    maxLength={50}
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                  <button
                    onClick={handleSubmit}
                    className={`${styles.submitButton} ${isMobile ? styles.mobileSubmitButton : ''} ${isSubmitting ? styles.submitButtonDisabled : ''}`}
                    disabled={isSubmitting}
                    type="button"
                  >
                    {isSubmitting ? t('messages.loading') : t('waitlist.joinButton')}
                  </button>
                </div>
                <div
                  className={`${styles.errorMessage} ${isMobile ? styles.mobileErrorMessage : ''}`}
                  style={{ opacity: emailError ? 1 : 0 }}
                >
                  {emailError || '\u00A0'}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div
            className={`${styles.successSection} ${isMobile ? styles.mobileSuccessSection : ''}`}
          >
            <div
              className={`${styles.successContent} ${isMobile ? styles.mobileSuccessContent : ''}`}
            >
              <h1 className={`${styles.successTitle} ${isMobile ? styles.mobileSuccessTitle : ''}`}>
                {t('waitlist.successTitle')}
              </h1>
              <div
                className={`${styles.successDescription} ${isMobile ? styles.mobileSuccessDescription : ''}`}
              >
                <span
                  className={`${styles.successText} ${isMobile ? styles.mobileSuccessText : ''}`}
                >
                  {t('waitlist.successMessage')}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WaitList;